import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ErrorToast, SuccessToast } from "../../shared/toasters/toasters";
import { Card, CardBody, Col } from "react-bootstrap";
import { usePagination } from "../../shared/hooks/usePagination";
import DeleteModal from "../../Common/DeleteModal";
import { CustomPagination } from "../../shared/components/CustomPagination";
import { FlatJobService } from "./service";
import { IJob } from "../../shared/interfaces/job/IJob";

const PER_PAGE_DEFAULT = 20;

const FlatJobList = () => {
  const { t } = useTranslation();
  const [list, setList] = useState<IJob[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [jobToDelete, setJobToDelete] = useState<number | null>(null);
  const {
    page,
    handlePages,
    pagination,
    totalPages,
    setTotalPages,
    goToPreviousPage,
    goToNextPage,
  } = usePagination({ perPage: PER_PAGE_DEFAULT });

  const fetchData = () => {
    FlatJobService.getAll(pagination)
      .then((response) => {
        setList(response.data ?? []);
        setTotalPages(response.totalPages);
      })
      .catch(ErrorToast);
  };

  useEffect(() => {
    fetchData();
  }, [pagination]);

  const handleMakeReal = (id: number) => {
    FlatJobService.updateStatus(id, false)
      .then(() => {
        SuccessToast(t("flatJob.madeRealSuccess"));
        fetchData();
      })
      .catch(ErrorToast);
  };

  const handleDeleteClick = (id: number) => {
    setJobToDelete(id);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = () => {
    if (jobToDelete !== null) {
      FlatJobService.delete(jobToDelete)
        .then(() => {
          SuccessToast(t("flatJob.deleteSuccess"));
          setShowDeleteModal(false);
          setJobToDelete(null);
          fetchData();
        })
        .catch(ErrorToast);
    }
  };

  return (
    <React.Fragment>
      <Col>
        <Card>
          <CardBody>
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>{t("job.id")}</th>
                    <th>{t("job.name")}</th>
                    <th>{t("flatJob.flat")}</th>
                    <th>{t("job.calculationType")}</th>
                    <th>{t("job.jobPrice")}</th>
                    <th>{t("job.updatedAt")}</th>
                    <th className="text-end">{t("job.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((item: any) => (
                    <tr key={item.id}>
                      <td>{item.id}</td>
                      <td>{item.name}</td>
                      <td>{item.flat?.name ?? "-"}</td>
                      <td>{item.calculationType ?? "-"}</td>
                      <td>{item.jobPrice != null ? item.jobPrice.toFixed(2) : "-"}</td>
                      <td>
                        {item.updatedAt
                          ? new Date(item.updatedAt).toLocaleDateString()
                          : "-"}
                      </td>
                      <td className="text-end">
                        <ul className="list-inline mb-0">
                          <li className="list-inline-item">
                            <button
                              className="btn btn-sm btn-success"
                              title={t("flatJob.makeReal")}
                              onClick={() => handleMakeReal(item.id)}
                            >
                              <i className="ti ti-check f-16 me-1"></i>
                              {t("flatJob.makeReal")}
                            </button>
                          </li>
                          <li className="list-inline-item">
                            <span
                              role="button"
                              className="avtar avtar-s btn-link-danger btn-pc-default"
                              onClick={() => handleDeleteClick(item.id)}
                            >
                              <i className="ti ti-trash f-20"></i>
                            </span>
                          </li>
                        </ul>
                      </td>
                    </tr>
                  ))}
                  {list.length === 0 && (
                    <tr>
                      <td colSpan={6} className="text-center text-muted py-4">
                        {t("general.loading")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <CustomPagination
              page={page}
              totalPages={totalPages}
              handlePages={handlePages}
              goToPreviousPage={goToPreviousPage}
              goToNextPage={goToNextPage}
            />
          </CardBody>
        </Card>
      </Col>
      <DeleteModal
        show={showDeleteModal}
        handleClose={() => setShowDeleteModal(false)}
        handleDeleteId={handleDeleteConfirm}
        name={t("flatJob.job")}
      />
    </React.Fragment>
  );
};

export default FlatJobList;
